import { useEffect, useMemo, useState } from "react";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

/**
 * useSortableTable — one-liner sortable table helper.
 *
 * @param {Array}  rows        The source rows (already filtered by the caller).
 * @param {Object} columns     Column config: { key: { get: row => value, type: "text"|"number"|"date" } }.
 * @param {Object} options     { defaultKey, defaultDir ("asc"|"desc"), storageKey (localStorage key) }.
 *
 * Rules:
 *  - First click on a column uses the type-based default direction: numbers/dates → desc, text → asc.
 *  - Subsequent clicks on the same column flip direction.
 *  - Clicking a different column resets to that column's default direction.
 *  - Persists the (key, dir) tuple to localStorage under `storageKey` if provided.
 */
export function useSortableTable(rows, columns, { defaultKey = null, defaultDir = null, storageKey = null } = {}) {
  const initial = () => {
    if (storageKey) {
      try {
        const raw = localStorage.getItem(storageKey);
        if (raw) {
          const p = JSON.parse(raw);
          if (p && p.key && p.dir && columns[p.key]) return { key: p.key, dir: p.dir };
        }
      } catch {}
    }
    return { key: defaultKey, dir: defaultDir };
  };
  const [sort, _setSort] = useState(initial);
  useEffect(() => {
    if (!storageKey) return;
    try { localStorage.setItem(storageKey, JSON.stringify(sort)); } catch {}
  }, [sort, storageKey]);

  const cycleSort = (colKey) => {
    const col = columns[colKey];
    if (!col) return;
    const initialDir = col.type === "text" ? "asc" : "desc";
    _setSort((prev) => {
      if (prev.key !== colKey) return { key: colKey, dir: initialDir };
      return { key: colKey, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const sortedRows = useMemo(() => {
    if (!sort.key || !columns[sort.key]) return rows;
    const col = columns[sort.key];
    const getter = col.get;
    const isText = col.type === "text";
    const dir = sort.dir === "asc" ? 1 : -1;
    // Stable sort using paired index tiebreaker
    return rows
      .map((r, i) => [r, i, getter(r)])
      .sort((a, b) => {
        const va = a[2], vb = b[2];
        const aNull = va === null || va === undefined || va === "";
        const bNull = vb === null || vb === undefined || vb === "";
        if (aNull && bNull) return a[1] - b[1];
        if (aNull) return 1;   // nulls always at the bottom
        if (bNull) return -1;
        let cmp;
        if (isText) cmp = String(va).localeCompare(String(vb), undefined, { sensitivity: "base", numeric: true });
        else if (col.type === "date") cmp = String(va).localeCompare(String(vb));  // ISO YYYY-MM-DD sorts lexically
        else cmp = (Number(va) || 0) - (Number(vb) || 0);
        return cmp !== 0 ? cmp * dir : a[1] - b[1];
      })
      .map((t) => t[0]);
  }, [rows, sort, columns]);

  return { sortedRows, sortKey: sort.key, sortDir: sort.dir, cycleSort };
}

/**
 * SortableTh — drop-in <th> replacement that renders the label + a directional arrow.
 * Click cycles sort. Pass the same `sortKey`/`sortDir`/`onClick` from `useSortableTable`.
 */
export function SortableTh({ label, colKey, sortKey, sortDir, onClick, className = "", align = "left", disabled = false }) {
  const isActive = sortKey === colKey;
  const alignCls = align === "right" ? "text-right justify-end" : align === "center" ? "text-center justify-center" : "text-left justify-start";
  return (
    <th className={`${className} ${align === "right" ? "text-right" : align === "center" ? "text-center" : ""}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && onClick(colKey)}
        data-testid={`sort-${colKey}`}
        className={`inline-flex items-center gap-1 group ${alignCls} ${disabled ? "cursor-default text-slate-400" : "cursor-pointer hover:text-slate-900"} transition-colors font-heading uppercase tracking-wider text-[11px] font-semibold ${isActive ? "text-slate-900" : "text-slate-500"} w-full`}
        title={disabled ? "Not sortable" : `Sort by ${label}`}
      >
        <span>{label}</span>
        {!disabled && (
          isActive ? (
            sortDir === "asc"
              ? <ArrowUp className="h-3 w-3 stroke-[2.5] text-brand" />
              : <ArrowDown className="h-3 w-3 stroke-[2.5] text-brand" />
          ) : (
            <ArrowUpDown className="h-3 w-3 opacity-30 group-hover:opacity-70" />
          )
        )}
      </button>
    </th>
  );
}
